import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { Coupon, CouponBulkCreatePayload, CouponPayload } from '../../../models/coupon';
import { CouponService } from '../../../services/coupon.service';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-form-coupon',
  imports: [CommonModule, FormsModule, RouterModule, AdminPageHeaderComponent],
  templateUrl: './form-coupon.component.html',
  styleUrl: './form-coupon.component.scss'
})
export class FormCouponComponent implements OnInit {
  title = 'Novo cupom';
  subtitle = 'Defina o desconto, as regras de uso e o público do cupom.';
  isEditMode = false;
  isSaving = false;
  couponId: number | null = null;
  feedbackError = '';
  isBulkCreate = false;
  bulkPrefix = '';
  bulkQuantity = 10;
  coupon: CouponPayload = {
    name: '',
    is_active: true,
    percentage_discount: null,
    fixed_discount: null,
    one_time_use: false,
    trusted_only: false,
    expiration_datetime: null,
    coupons_max_usage: 0,
    account_id: null,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private couponService: CouponService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.couponId = Number(id);
      this.title = 'Editar cupom';
      this.loadCoupon(this.couponId);
    }
  }

  loadCoupon(id: number): void {
    this.couponService.get(id).subscribe({
      next: (coupon: Coupon) => {
        this.coupon = {
          name: coupon.name || '',
          is_active: coupon.is_active ?? true,
          percentage_discount: coupon.percentage_discount ?? null,
          fixed_discount: coupon.fixed_discount ?? null,
          one_time_use: coupon.one_time_use ?? false,
          trusted_only: coupon.trusted_only ?? false,
          expiration_datetime: coupon.expiration_datetime
            ? this.toDatetimeLocal(coupon.expiration_datetime)
            : null,
          coupons_max_usage: coupon.coupons_max_usage ?? 0,
          account_id: coupon.account_id ?? coupon.account?.id ?? null,
        };
      },
      error: () => (this.feedbackError = 'Não foi possível carregar o cupom.'),
    });
  }

  submit(): void {
    this.feedbackError = '';
    const hasPercentageDiscount = !!this.coupon.percentage_discount;
    const hasFixedDiscount = !!this.coupon.fixed_discount;
    if (!this.isBulkCreate && !this.coupon.name.trim()) {
      this.feedbackError = 'Informe o nome do cupom.';
      return;
    }
    if (this.isBulkCreate && (!this.bulkPrefix.trim() || this.bulkPrefix.trim().length > 10)) {
      this.feedbackError = 'Informe um prefixo de até 10 caracteres.';
      return;
    }
    if (hasPercentageDiscount === hasFixedDiscount) {
      this.feedbackError = 'Informe somente um tipo de desconto.';
      return;
    }

    this.isSaving = true;
    const payload: CouponPayload = {
      ...this.coupon,
      name: this.coupon.name.trim(),
      expiration_datetime: this.coupon.expiration_datetime
        ? new Date(this.coupon.expiration_datetime).toISOString()
        : null,
    };
    const { name: _name, ...bulkPayload } = payload;
    const request: Observable<unknown> = this.isBulkCreate
      ? this.couponService.bulkCreate({
          ...bulkPayload,
          prefix: this.bulkPrefix.trim(),
          quantity: this.bulkQuantity,
        })
      : this.isEditMode && this.couponId
        ? this.couponService.update(this.couponId, payload)
        : this.couponService.create(payload);
    request.subscribe({
      next: () => this.router.navigate(['/dashboard/cupons']),
      error: (error: unknown) => {
        this.isSaving = false;
        this.feedbackError = this.extractErrorMessage(error);
      },
    });
  }

  private extractErrorMessage(error: any): string {
    const response = error?.error;
    if (typeof response?.detail === 'string') {
      return response.detail;
    }
    if (response && typeof response === 'object') {
      return Object.entries(response)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join(' | ');
    }
    return 'Não foi possível salvar o cupom.';
  }

  private toDatetimeLocal(value: string): string {
    const date = new Date(value);
    const pad = (number: number) => String(number).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
